// Google Tag Manager. The container (GTM_ID, e.g. GTM-5VZT9RL2) holds the GA4 tag and any
// others; this only loads it and pushes page views, events and the signed-in user into
// window.dataLayer. Nothing loads without a GTM id, so builds without one send nothing.
const CONTAINER_ID = process.env.GTM_ID;

const enabled = () => typeof window !== 'undefined' && /^GTM-[A-Z0-9]+$/.test(CONTAINER_ID || '');

function push(data) {
    window.dataLayer.push(data);
}

// GTM merges every push into one data model, so an event's parameters would otherwise
// still be set for later events (a checklist_slug on the next page view). Each event clears
// the previous event's parameters, apart from these, which describe the current page.
const PAGE_KEYS = ['page_location', 'page_path', 'page_title'];
let previousEventKeys = [];

function clearedPreviousParams() {
    const cleared = {};
    previousEventKeys.forEach((key) => { cleared[key] = undefined; });
    return cleared;
}

// The email verification and password reset links carry one-time tokens that must not
// reach Google Analytics
const PRIVATE_PARAMS = ['confirmation_token', 'reset_token'];

function sanitisedUrl() {
    const { href, origin, pathname } = window.location;
    try {
        const url = new URL(href);
        PRIVATE_PARAMS.forEach(param => url.searchParams.delete(param));
        return url;
    } catch (e) {
        // browsers without URL support lose the query string rather than leak a token
        return { href: origin + pathname, pathname, search: '' };
    }
}

// account_type is the plan name; subscription_status groups users for the trial funnel
function subscriptionStatus(user) {
    if (user.account_type !== 'Starter') return 'subscriber';
    return user.free_trial_expired ? 'trial_expired' : 'trial';
}

const Analytics = {
    init: () => {
        if (!enabled()) return;

        window.dataLayer = window.dataLayer || [];
        push({ 'gtm.start': new Date().getTime(), event: 'gtm.js' });

        const script = document.createElement('script');
        script.async = true;
        script.src = `https://www.googletagmanager.com/gtm.js?id=${CONTAINER_ID}`;
        document.head.appendChild(script);
    },

    // Pass a callback when the page is about to unload: it runs once GTM has fired the
    // event's tags, or after a second if GTM never loaded (an ad blocker, say).
    // Events with items are ecommerce events and go in GA4's ecommerce object.
    event: (name, params = {}, callback) => {
        if (!enabled()) {
            if (callback) callback();
            return;
        }

        let done = false;
        const finish = () => {
            if (done || !callback) return;
            done = true;
            callback();
        };

        const data = params.items
            ? { ...clearedPreviousParams(), event: name, ecommerce: params }
            : { ...clearedPreviousParams(), event: name, ...params };
        previousEventKeys = params.items ? [] : Object.keys(params).filter(key => !PAGE_KEYS.includes(key));
        if (params.items) push({ ecommerce: null }); // clear the previous ecommerce event's data
        if (callback) {
            data.eventCallback = finish;
            data.eventTimeout = 1000;
            setTimeout(finish, 1000);
        }
        push(data);
    },

    pageView: () => {
        if (!enabled()) return;
        const url = sanitisedUrl();
        // The router reports a route change before React Helmet sets the new page's title,
        // which it does in an animation frame, so wait for that before reading the title.
        // GTM's tags read page_location from here, so the token-free address is what's sent.
        const send = () => Analytics.event('page_view', {
            page_location: url.href,
            page_path: url.pathname + url.search,
            page_title: document.title
        });
        if (typeof window.requestAnimationFrame !== 'function') {
            setTimeout(send, 0);
            return;
        }
        window.requestAnimationFrame(() => setTimeout(send, 0));
    },

    // Ties events to the account so usage can be split by plan. Only the numeric id and
    // these properties are sent: never the email, name or session token.
    identifyUser: (user) => {
        if (!enabled() || !user || !user.id) return;
        push({
            event: 'user_data',
            user_id: String(user.id),
            user_properties: {
                account_type: user.account_type || null,
                subscription_status: subscriptionStatus(user),
                is_academic: user.is_academic ? 'yes' : 'no'
            }
        });
    },

    reset: () => {
        if (!enabled()) return;
        push({
            event: 'user_data',
            user_id: null,
            user_properties: { account_type: null, subscription_status: null, is_academic: null }
        });
    }
};

export default Analytics;
